import { useRef } from 'preact/compat';
import { useEffect, useState } from 'preact/hooks';
import { AdjustmentsHorizontal } from 'flowbite-react-icons/solid';
import { useSignalWithLocalStorage } from '@util/deepSignals.js';
import useTiltifyConfig from '@hooks/useTiltifyConfig.js';
import useTwitchConfig from '@hooks/useTwitchConfig.js';

export default function DevModal() {
  const tiltifyConfig = useTiltifyConfig();
  const twitchConfig = useTwitchConfig();
  const [error, setError] = useState(null);
  const textAreaRef = useRef();
  const setTiltifyConfigState = useSignalWithLocalStorage(tiltifyConfig);
  const setTwitchConfigState = useSignalWithLocalStorage(twitchConfig);
  const splitHost = window.location.host.split('.');
  const modalRef = useRef(null);
  let environment = 'prod';

  if (splitHost.length > 1) {
    const hostEnvironment = splitHost[1];

    switch (hostEnvironment) {
      case 'dev':
      case 'qa':
        environment = hostEnvironment;
        break;
      case 'experience':
        environment = 'prod';
    }
  } else environment = import.meta.env.VITE_APP_ENV;

  useEffect(() => {
    const handleModalClose = () => {
      document.body.classList.remove('show-modal');
    };

    const modal = modalRef.current;

    if (modal) modal.addEventListener('close', handleModalClose);
    return () => {
      modal.removeEventListener('close', handleModalClose);
    };
  }, []);

  function setModalRef(div) {
    modalRef.current = div;
  }

  function showModal() {
    if (modalRef.current) {
      document.body.classList.add('show-modal');
      modalRef.current.showModal();
    }
  }

  function resetSettings(event) {
    event.preventDefault();
    event.stopPropagation();

    const emptyConfig = {
      ...tiltifyConfig,
      ...twitchConfig,
    };

    Object.keys(emptyConfig).forEach((key) => {
      if (key === 'tiltifyCampaignGoal') emptyConfig[key] = '0';
      else emptyConfig[key] = '';
    });

    parseSetConfig(JSON.stringify(emptyConfig));
  }

  function applyChanges(event) {
    event.preventDefault();
    event.stopPropagation();
    parseSetConfig(textAreaRef.current.value);
  }

  function parseSetConfig(configString) {
    setError(null);
    try {
      const newConfig = JSON.parse(configString);

      Object.keys(newConfig).forEach((key) => {
        if (key.includes('tiltify')) {
          setTiltifyConfigState({
            target: { name: key, value: newConfig[key] },
          });
        } else if (key.includes('twitch')) {
          setTwitchConfigState({
            target: { name: key, value: newConfig[key] },
          });
        }
      });
    } catch (e) {
      console.error(e.message);
      setError(e.message ?? 'Invalid JSON');
    }
  }

  const textareaValue = JSON.stringify(
    {
      ...tiltifyConfig,
      ...twitchConfig,
    },
    null,
    2
  );

  if (environment !== 'dev') return <></>;

  return (
    <div className="absolute bottom-3 right-3 z-50 pt-5 pl-5">
      <button
        className="btn btn-circle btn-secondary mr-4 shadow-xl"
        onClick={showModal}
      >
        <AdjustmentsHorizontal />
      </button>
      <dialog ref={setModalRef} id="devModal" className="modal">
        <div className="modal-box">
          <h3 className="text-lg font-bold">Developer Settings</h3>
          <div className="relative mt-1">
            <textarea
              ref={(textarea) => (textAreaRef.current = textarea)}
              spellcheck="false"
              className="textarea textarea-bordered text-sm w-full min-h-96 overflow-x-hidden overflow-y-auto resize-none"
              placeholder="{}"
              value={textareaValue}
            />

            <div className="min-h-10">
              <span className="text-xs text-error">{error ? error : ''}</span>
            </div>
          </div>
          <div className="modal-action">
            <form method="dialog" className="flex w-full">
              <button
                className="btn btn-sm btn-primary mr-1"
                onClick={resetSettings}
              >
                Reset
              </button>
              <span className="flex-grow" />
              <button
                className="btn btn-sm btn-secondary mr-1"
                onClick={applyChanges}
              >
                Apply
              </button>
              <button className="btn btn-sm btn-neutral">Close</button>
            </form>
          </div>
        </div>
        <form method="dialog" className="modal-backdrop">
          <button>close</button>
        </form>
      </dialog>
    </div>
  );
}
